import { AlertTriangle, Check, Info, X } from "lucide-react";
import { useMemo } from "react";
import {
  AnimatedToastStack,
  type AnimatedToast,
  type ToastStatus,
} from "../motion/animated-toast-stack";
import { FintheonNumber } from "../numbers/FintheonNumber";
import { FintheonNumericText } from "../numbers/FintheonNumericText";
import { useToast } from "../../contexts/ToastContext";
import { uiLibrarySourceAttrs } from "../../lib/ui-library/ui-library-source-attrs";
import { uiLibraryThemeStyle } from "../../lib/ui-library/ui-library-theme";
import { DotMatrixLoader } from "../icon-bank/DotMatrixLoader";
import type { NotificationEvent } from "./notification-event";
import { notificationToastPosition } from "./notification-event";

function toastStatus(event: NotificationEvent): ToastStatus {
  if (event.severity === "success") return "success";
  if (event.severity === "loading") return "loading";
  if (event.severity === "error" || event.severity === "critical")
    return "error";
  if (event.severity === "info" || event.severity === "warning") return "info";
  return "neutral";
}

function EventMark({ event }: { event: NotificationEvent }) {
  const props = { size: 14, strokeWidth: 1.8 };
  if (event.severity === "success") return <Check {...props} />;
  if (event.severity === "loading")
    return (
      <DotMatrixLoader
        variant="loading"
        size={14}
        label="Loading notification"
      />
    );
  if (
    event.severity === "warning" ||
    event.severity === "error" ||
    event.severity === "critical"
  ) {
    return <AlertTriangle {...props} />;
  }
  if (event.severity === "info") return <Info {...props} />;
  return <Info {...props} />;
}

export function NotificationStackPresenter({
  mobile = false,
}: {
  mobile?: boolean;
}) {
  const { visibleEvents, dismissToast, dismissAllTransient, markEventRead } =
    useToast();
  const byId = useMemo(
    () => new Map(visibleEvents.map((event) => [event.id, event])),
    [visibleEvents],
  );
  const lanes = useMemo(() => {
    const next = {
      bottomLeft: [] as NotificationEvent[],
      bottomRight: [] as NotificationEvent[],
    };
    visibleEvents.forEach((event) => {
      const position = notificationToastPosition(event);
      if (position === "bottom-right") next.bottomRight.push(event);
      else next.bottomLeft.push(event);
    });
    return next;
  }, [visibleEvents]);
  const toastsFor = (events: NotificationEvent[]) =>
    events.map<AnimatedToast>((event) => ({
      id: event.id,
      title: event.title,
      description: event.body,
      status: toastStatus(event),
      createdAt: event.updatedAt,
      duration: 0,
      dismissible: event.persistence !== "acknowledgement",
    }));

  if (visibleEvents.length === 0) return null;
  const newestEventId = visibleEvents.reduce((newest, event) =>
    event.updatedAt > newest.updatedAt ? event : newest,
  ).id;
  const transientCount = visibleEvents.filter(
    (item) => item.persistence === "transient",
  ).length;

  const renderLane = (
    events: NotificationEvent[],
    position: "bottom-left" | "bottom-right",
  ) => {
    if (events.length === 0) return null;
    const toasts = toastsFor(events);
    const stacked = events.length > 1;
    const right = position === "bottom-right";
    const mobileBackendDown =
      mobile && events.some((event) => event.notificationType === "api-error");
    return (
      <AnimatedToastStack
        key={position}
        toasts={toasts}
        onDismiss={dismissToast}
        position={position}
        placement="fixed"
        maxVisible={3}
        newestFirst
        stacked={stacked}
        className={
          mobile
            ? `${right ? "!right-3" : "!left-3"} ${mobileBackendDown ? "!top-[calc(env(safe-area-inset-top,0px)+1rem)] !bottom-auto" : "!bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)]"} !w-[calc(100vw-1.5rem)]`
            : right
              ? "!bottom-14 !right-4"
              : "!bottom-14 !left-14"
        }
        classNames={{
          root: "s138-notification-stack",
          surface:
            "!overflow-visible !rounded-lg !border-0 !bg-transparent !p-0 !shadow-none !backdrop-blur-none",
        }}
        renderToast={(toast) => {
          const event = byId.get(toast.id);
          return event ? (
            <NotificationEventCard
              event={event}
              transientCount={transientCount}
              showCloseAll={stacked && event.id === newestEventId}
              onDismiss={dismissToast}
              onDismissAll={dismissAllTransient}
              onRead={markEventRead}
            />
          ) : null;
        }}
      />
    );
  };

  return (
    <div
      {...uiLibrarySourceAttrs("beui")}
      data-ui-library-surface
      data-notification-presenter
      style={uiLibraryThemeStyle()}
    >
      {renderLane(lanes.bottomLeft, "bottom-left")}
      {renderLane(lanes.bottomRight, "bottom-right")}
    </div>
  );
}

function NotificationEventCard({
  event,
  transientCount,
  showCloseAll,
  onDismiss,
  onDismissAll,
  onRead,
}: {
  event: NotificationEvent;
  transientCount: number;
  showCloseAll: boolean;
  onDismiss: (id: string) => void;
  onDismissAll: () => void;
  onRead: (id: string) => void;
}) {
  const dismissible = event.persistence !== "acknowledgement";
  const runAction = (action: NonNullable<NotificationEvent["action"]>) => {
    action.onClick();
    onRead(event.id);
    if (event.persistence === "transient") onDismiss(event.id);
  };

  return (
    <article
      data-notification-id={event.id}
      data-repeat-count={event.repeatCount}
      className="fintheon-toast-surface solvys-liquid-glass fintheon-black-liquid-glass notification-stack-liquid-glass relative w-full min-w-0 overflow-visible rounded-lg border-0"
    >
      <div className="flex min-h-[74px] items-start gap-2.5 px-3 py-2.5">
        <span
          className="fintheon-toast-mark mt-0.5 shrink-0"
          aria-hidden="true"
        >
          <EventMark event={event} />
        </span>
        <div className="min-w-0 flex-1 pr-24 md:pr-14">
          <div className="flex items-baseline gap-1.5">
            <p className="min-w-0 text-[12px] font-semibold leading-4 text-[var(--fintheon-text)]">
              <FintheonNumericText text={event.title} />
            </p>
            {event.repeatCount > 1 ? (
              <span
                className="shrink-0 text-[10px] text-[var(--fintheon-muted)]"
                aria-label={`Repeated ${event.repeatCount} times`}
              >
                ×<FintheonNumber value={event.repeatCount} kind="count" />
              </span>
            ) : null}
          </div>
          {event.body ? (
            <p className="mt-0.5 line-clamp-2 text-[10px] leading-4 text-[var(--fintheon-muted)]">
              <FintheonNumericText text={event.body} />
            </p>
          ) : null}
          {event.action || event.secondaryAction ? (
            <div className="fintheon-toast-actions fintheon-toast-hover-actions mt-1">
              {event.secondaryAction ? (
                <button
                  type="button"
                  className="fintheon-toast-action-button fintheon-toast-action-button--secondary"
                  onClick={() => runAction(event.secondaryAction!)}
                  title={event.secondaryAction.label}
                >
                  {event.secondaryAction.label}
                </button>
              ) : null}
              {event.action ? (
                <button
                  type="button"
                  className="fintheon-toast-action-button fintheon-toast-action-button--primary"
                  onClick={() => runAction(event.action!)}
                  title={event.action.label}
                >
                  {event.action.label}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="fintheon-toast-hover-actions absolute right-2 top-2 flex items-center gap-0">
          {showCloseAll && transientCount > 0 ? (
            <button
              type="button"
              onClick={onDismissAll}
              className="s156-notification-action-control fintheon-toast-action-button fintheon-toast-action-button--secondary min-h-7 px-1.5 text-[10px]"
              aria-label={`Close all ${transientCount} transient notifications`}
              title="Close all transient notifications"
            >
              Close All
            </button>
          ) : null}
          {dismissible ? (
            <button
              type="button"
              onClick={() => onDismiss(event.id)}
              className="s156-notification-icon-control inline-flex size-8 items-center justify-center"
              aria-label="Close notification"
              title="Close notification"
            >
              <X aria-hidden="true" size={12} />
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
